import { CircleAlert, RotateCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ApiError, getApiErrorMessage } from '@/lib/api-error'
import { cn } from '@/lib/utils'

export interface ErrorStateProps {
    error: unknown
    onRetry?: () => void
    /** What the operator was trying to do, used when the API gives no message. */
    context?: string
    className?: string
}

/**
 * The legacy app swallowed fetch failures into `console.error` on five pages,
 * including the users list, so a failed request looked identical to an empty
 * result. This always says something, and preserves the backend's own wording
 * when there is any -- operators act on that exact text.
 */
export function ErrorState({ error, onRetry, context, className }: ErrorStateProps) {
    const message = getApiErrorMessage(error, context ? `Could not load ${context}.` : 'Something went wrong.')
    const isApiError = error instanceof ApiError

    return (
        <div
            role="alert"
            className={cn('flex flex-col items-center justify-center gap-3 px-6 py-16 text-center', className)}>
            <div
                aria-hidden
                className="flex size-10 items-center justify-center rounded-lg border border-destructive-soft bg-destructive-soft text-destructive-soft-foreground">
                <CircleAlert className="size-5" />
            </div>
            <p className="text-sm font-medium text-foreground">
                {isApiError && error.isForbidden
                    ? 'You do not have permission to view this'
                    : context
                      ? `Could not load ${context}`
                      : 'Request failed'}
            </p>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground">{message}</p>

            {isApiError && error.status > 0 && (
                <p className="font-id text-2xs text-muted-foreground">HTTP {error.status}</p>
            )}

            {/* A 4xx will answer the same way on retry, so offering the button would
                only invite the operator to waste a click. */}
            {onRetry && (!isApiError || error.isRetryable) && (
                // The only control in the panel, with nothing within a finger's width of
                // it, so hit-slop is right here: the button does not move or resize.
                <Button variant="outline" size="sm" onClick={onRetry} className="mt-1 touch-target">
                    <RotateCw />
                    Try again
                </Button>
            )}
        </div>
    )
}
